import { getDay } from 'date-fns';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Grid } from 'src/components/Grid';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { RecurringRuleFields } from 'src/shared/Appointments/RecurringRuleFields';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { DEFAULT_RECURRING_DAYS, EditEventFormValues } from './_types';

import type { Service } from 'src/models/service/_types';
import type { Identifier } from 'src/types/utilityTypes';

interface RecurringSettingsSectionProps {
  getServiceById: (serviceId: Identifier | null) => Service | undefined;
  isRecurring?: boolean;
}

const SESSION_FIELD_NAMES = {
  recurringCount: 'recurringCount',
  recurringType: 'recurringType',
  recurringDayNumbers: 'recurringDayNumbers',
  days: 'recurringDays',
} as const;

export const RecurringSettingsSection: FC<RecurringSettingsSectionProps> = ({
  getServiceById,
  isRecurring = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.editEventDialog.recurringSettings',
  });
  const { values, setFieldValue } = useFormikContext<EditEventFormValues>();

  const selectedService = getServiceById(values.serviceId);
  const isGroupClass = selectedService?.serviceType === 'group_class';
  const showPerDayMaxVisits =
    values.isRecurring &&
    isGroupClass &&
    values.recurringType === 'week';

  // The weekday of the session instance the user opened cannot be deselected
  // (legacy v2 currentScheduledDays). Other days in a multi-day recurrence
  // stay toggleable.
  const scheduledDay = values.startTime ? getDay(values.startTime) : undefined;

  const addMaxVisitDays = () => {
    const recurringDays =
      values.recurringDays.length > 0
        ? values.recurringDays
        : [getDay(values.startTime)];

    setFieldValue(
      'maxVisitDays',
      recurringDays.map((dayId) => {
        const existing = values.maxVisitDays.find((entry) => entry.id === dayId);
        if (existing) return existing;

        return {
          id: dayId,
          maxVisits: values.maxVisits ?? values.seriesMaxVisits ?? null,
        };
      })
    );
  };

  const removeMaxVisitDays = () => {
    setFieldValue('maxVisitDays', []);
  };

  const updateMaxVisitDay = (dayId: number, rawValue: string) => {
    const parsed =
      rawValue === '' ? null : Number.parseInt(rawValue, 10);

    setFieldValue(
      'maxVisitDays',
      values.maxVisitDays.map((entry) =>
        entry.id === dayId
          ? {
              ...entry,
              maxVisits: Number.isNaN(parsed) ? null : parsed,
            }
          : entry
      )
    );
  };

  return (
    <Stack spacing={2}>
      {!isRecurring && (
        <Checkbox
          useSwitch
          label={t('makeRecurring')}
          checked={values.isRecurring}
          onChange={(e) => setFieldValue('isRecurring', e.target.checked)}
        />
      )}

      {values.isRecurring && (
        <Box
          component="fieldset"
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
            m: 0,
          }}
        >
          <Typography variant="h6" component="legend">
            {t('title')}
          </Typography>

          {isRecurring && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('isRecurringNote')}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                {t('onlyThisSessionNote')}
              </Typography>
            </>
          )}

          <Stack spacing={3} sx={{ mt: 1 }}>
            <RecurringRuleFields
              fieldNames={SESSION_FIELD_NAMES}
              defaultDay={getDay(values.startTime)}
              lockedDay={scheduledDay}
            />

            {isGroupClass && (
              <FormTextField
                name="seriesMaxVisits"
                label={t('seriesMaxSignups')}
                type="number"
                placeholder={t('unlimitedPlaceholder')}
                helperText={t('seriesMaxSignupsHelper')}
              />
            )}

            {showPerDayMaxVisits && (
              <Box>
                {values.maxVisitDays.length > 0 ? (
                  <>
                    <Typography variant="subtitle2">
                      {t('perDayMaxSignupsTitle')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('perDayMaxSignupsHelper')}
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      onClick={removeMaxVisitDays}
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {t('removePerDayOverride')}
                    </Link>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {values.maxVisitDays.map((entry) => {
                        const dayName =
                          DEFAULT_RECURRING_DAYS.find((day) => day.id === entry.id)
                            ?.name ?? '';

                        return (
                          <Grid key={entry.id} size={{ xs: 6, sm: 4, md: 3 }}>
                            <TextField
                              label={`${dayName} ${t('maxLabel')}`}
                              type="number"
                              placeholder={t('unlimitedPlaceholder')}
                              value={entry.maxVisits ?? ''}
                              onChange={(event) =>
                                updateMaxVisitDay(entry.id, event.target.value)
                              }
                              fullWidth
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </>
                ) : (
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={addMaxVisitDays}
                  >
                    {t('addPerDayOverride')}
                  </Link>
                )}
              </Box>
            )}

            <FormDatePicker
              name="endAt"
              label={t('endDate')}
              minDate={values.startTime}
            />

            <Box>
              <Checkbox
                label={t('allowBookAllRecurring')}
                checked={values.bookRecurring}
                onChange={(e) =>
                  setFieldValue('bookRecurring', e.target.checked)
                }
              />
            </Box>
          </Stack>
        </Box>
      )}
    </Stack>
  );
};
